import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import type { CustomVariablesDataPayload } from '@openx/types/targeting/customVariables.ts';
import { ListIconType } from '@openx/types/targeting/targeting.ts';
import type { CriteriaValue } from '@openx/types/targeting/targetingValuesTypes.ts';
import { Fragment, type JSX, useMemo } from 'react';
import { mapTypeToIconType, useComparisonLabels } from '../constants.ts';
import { DimensionChip } from '../shared/DimensionChip/DimensionChip.tsx';
import { SelectedItems } from '../shared/SelectedItems/SelectedItems.tsx';

const StyledSpan = styled('span')`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

type CustomVariablesListProps = {
  groupValuesByComparisonType: Record<string, CustomVariablesDataPayload[]>;
  handleRemove: (comparisonType: string, newState: CustomVariablesDataPayload[]) => void;
  readonly: boolean;
  op: CriteriaValue;
};

export const CustomVariablesList = ({
  groupValuesByComparisonType,
  handleRemove,
  readonly,
  op,
}: CustomVariablesListProps): JSX.Element => {
  const comparisonLabels = useComparisonLabels();

  const listItems = useMemo(() => {
    const optionsEntries = Object.entries(groupValuesByComparisonType);

    return optionsEntries.map(([comparisonType, items], idx) => {
      return (
        <Fragment key={comparisonType}>
          {items.length ? (
            <Grid data-test={`section-items-${comparisonLabels[comparisonType].toLowerCase().replaceAll(' ', '-')}`}>
              <SelectedItems
                filtersTitle={comparisonLabels[comparisonType]}
                isAddIcon={mapTypeToIconType[comparisonType] === ListIconType.INCLUDE}
                selectedItems={items}
                readonly={readonly}
                handleChange={newState => handleRemove(comparisonType, newState)}
                getItemLabel={item => (
                  <>
                    <span>{`${item.attr} `}</span>
                    <StyledSpan>{`${comparisonLabels[item.op].toLowerCase()} `}</StyledSpan>
                    <span>{item.val}</span>
                  </>
                )}
                isRemovingAll={true}
                dataTest={'custom-variables'}
              />
              {idx < optionsEntries.length - 1 && <DimensionChip isInsideOption={true} targetingDimension={op} />}
            </Grid>
          ) : null}
        </Fragment>
      );
    });
  }, [handleRemove, readonly, groupValuesByComparisonType, op, comparisonLabels]);

  return <>{listItems}</>;
};
